import { useContext, useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import { CircularProgress } from "@mui/material";
import lodash from "lodash";
import json5 from "json5";
import TextInput from "./../../Component/UI/TextInput/TextInput";
import MUIButton from "../../Component/UI/MUIButton/MUIButton";
import RadioGroup from "../../Component/UI/RadioGroup/RadioGroup";
import MUISelect from "../../Component/UI/MUISelect/MUISelect";
import classes from "./Form.module.css";
import { BuyingJourneyContext } from "./buying-journey-context";
import { LLM_OPTIONS } from "../../utils/constant";
import { validateBrandInput, validateProductInput } from "../../utils/helpers";
import Warning from "../../Component/Warning/Warning";
import Modal from "../../Component/Modal/Modal";
import { fetchGpt4API } from "../../services/apiService";
import useTopUserProducts from "../Discovery/useTopUserProducts";

const RADIO_OPTIONS = [
  {
    value: "brand",
    label: "Brand",
  },
  {
    value: "product",
    label: "Product",
  },
];

export default function Form({
  onSubmit,
  onTypeChanged = () => {},
  onReset = () => {},
  warningText,
  setWarningText = () => {},
}) {
  const discoveryCtx = useContext(BuyingJourneyContext);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const competitorsRef = useRef(discoveryCtx.competitors);

  const [brand, setBrand] = useState("");
  const [discoveryType, setDiscoveryType] = useState("brand");
  const [llm, setLLM] = useState("gpt-4o");
  const [category, setCategory] = useState("");
  const [market, setMarket] = useState("");

  const [showManualCategoryInput, setShowManualCategoryInput] = useState(false);
  const [manualCategoryInput, setManualCategoryInput] = useState("");
  const [aICategories, setAICategories] = useState([]);
  const [isCategorySuggestionLoading, setCategorySuggestionLoading] =
    useState(false);
  const categoryChangeTimerRef = useRef();
  const keyPlayerChangeTimerRef = useRef();

  const { loading: keyPlayersLoading, getBrandOrProductSuggestions } =
    useTopUserProducts();

  const timerRef = useRef();

  useEffect(() => {
    if (!category || discoveryCtx.state.id) return;
    if (categoryChangeTimerRef.current) {
      clearTimeout(categoryChangeTimerRef.current);
    }
    discoveryCtx.updateProductPrefs([]);
  }, [category, llm, brand]);

  useEffect(() => {
    if (!brand || discoveryCtx.is_launched) return;
    async function getCategoryAISuggestions() {
      setCategorySuggestionLoading(true);
      try {
        const prompt = `For the brand or product name '${brand}', return the top 5 categories.
        Strictly format the response as follows:
        {"categories": ["Category1", "Category2", "Category3", "Category4", "Category5"]}
        Do not include explanations, descriptions, or any additional text.`;
        let result = await fetchGpt4API(
          prompt,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          "Discovery"
        );
        result = result["GPT-4"][0];

        if (result) {
          let resultArray = json5.parse(result);
          setAICategories(
            resultArray.categories.map((o) => ({ name: o, value: o }))
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        setCategorySuggestionLoading(false);
      }
    }

    const debouncedFunction = lodash.debounce(getCategoryAISuggestions, 2000);
    debouncedFunction();
    return () => debouncedFunction.cancel();
  }, [brand, discoveryCtx.is_launched]);

  useEffect(() => {
    if (!category || discoveryCtx.state.id || !brand) {
      return;
    }

    keyPlayerChangeTimerRef.current = setTimeout(() => {
      if (discoveryCtx.type === "category" || discoveryCtx.type === "product")
        getBrandOrProductSuggestions(
          "product",
          category,
          null,
          [],
          null,
          market,
          brand
        );
      if (discoveryCtx.type === "brand")
        getBrandOrProductSuggestions(
          "brand",
          category,
          null,
          [],
          null,
          market,
          brand
        );
    }, 1000);
  }, [discoveryCtx.state.id, brand, discoveryType, category, market, llm]);

  async function checkIfInputValid(input) {
    setLoading(true);
    setSuggestions([]);
    setWarningText("");
    try {
      let response = await validateBrandInput(input, category);
      setLoading(false);
      const latestCompetitors = competitorsRef.current;
      if (response.data.ans === "Yes") {
        const updatedCompetitors = [...latestCompetitors];
        updatedCompetitors.unshift({
          name: brand,
          domain: response.data.domain,
        });
        discoveryCtx.updatedCompetitors(updatedCompetitors);
      } else {
        setWarningText(
          `The ${discoveryType} or category name appears to be incorrect. This may impact the result.`
        );
        setSuggestions(response?.data?.correct_inputs);
        discoveryCtx.updatedCompetitors([
          { name: input, domain: "example.com" },
          ...discoveryCtx.competitors,
        ]);
      }
    } catch (error) {
      console.log("Error while validating input", error);
      setLoading(false);
    }
  }

  async function checkIfProductValid(input) {
    setLoading(true);
    setSuggestions([]);
    setWarningText("");

    try {
      let response = await validateProductInput(input, category);
      setLoading(false);

      const latestCompetitors = competitorsRef.current;
      if (response.data.ans === "Yes") {
        const updatedCompetitors = [...latestCompetitors];
        updatedCompetitors.unshift({
          name: brand,
          domain: response.data.domain,
          variants: [],
        });

        discoveryCtx.updatedCompetitors(updatedCompetitors);
      } else {
        if (
          new String(response?.data?.correct_inputs[0])
            .toLocaleLowerCase()
            .trim() !== new String(discoveryCtx.entity).toLowerCase().trim()
        ) {
          setSuggestions(response?.data?.correct_inputs);
          setWarningText(
            `The ${discoveryType} or category name appears to be incorrect. This may impact the Brand Index report.`
          );
        }
        discoveryCtx.updatedCompetitors([
          { name: input, domain: "example.com" },
          ...discoveryCtx.competitors,
        ]);
      }
    } catch (error) {
      console.log("Error while validating input", error);
      setLoading(false);
    }
  }

  function handleSuggestionClick(text) {
    setBrand(text);
    setSuggestions([]);
  }

  function handleBrandInputChange(e) {
    setBrand(e.target.value);
    resetState();
  }

  function resetState() {
    discoveryCtx.updateAbortLaunch(true);

    discoveryCtx.updateId(null);
    discoveryCtx.updatedLaunched(false);
  }

  function handleDiscoveryTypeChange(value) {
    discoveryCtx.resetState();
    onTypeChanged(value);
    discoveryCtx.updateType(value);
    setDiscoveryType(value);
    setBrand("");
    setCategory("");
    setLLM("gpt-4o");
    setSuggestions([]);

    onReset();
  }

  useEffect(() => {
    if (
      !brand ||
      discoveryCtx.is_launched ||
      !category ||
      discoveryCtx.state.id
    ) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (discoveryType === "product") {
        checkIfProductValid(brand);
      } else {
        checkIfInputValid(brand);
      }
    }, 1000);
  }, [
    brand,
    category,
    discoveryType,
    market,
    llm,
    discoveryCtx.is_launched,
    discoveryCtx.state.id,
  ]);

  useEffect(() => {
    if (discoveryCtx.state.id) {
      setBrand(discoveryCtx.entity);
      setAICategories([
        {
          name: discoveryCtx.category,
          value: discoveryCtx.category,
        },
      ]);
      setCategory(discoveryCtx.category);
      setMarket(discoveryCtx.market);
      setLLM(discoveryCtx.llm);
    }
  }, [
    discoveryCtx.state.id,
    discoveryCtx.category,
    discoveryCtx.llm,
    discoveryCtx.entity,
    discoveryCtx.market,
  ]);

  const debounceLaunch = lodash.debounce(function () {
    if (!brand || !category || !llm) {
      toast.warn("Please fill all required fields");
      return;
    }

    if (discoveryCtx.loading) {
      discoveryCtx.updateAbortLaunch(true);
      return;
    }

    onSubmit({
      brand,
      category,
      market,
      llm,
      discoveryType,
    });
  }, 1000);

  return (
    <div className={classes.form}>
      <div className={classes.header}>
        <div className={classes.radio}>
          <RadioGroup
            disabled={discoveryCtx.loading}
            className={classes.type_radio}
            options={RADIO_OPTIONS}
            onChange={handleDiscoveryTypeChange}
          />
        </div>
        {warningText && <Warning text={warningText} />}
      </div>

      <div className={classes.input_group}>
        <div className={classes.input_section}>
          <div className={classes.brand_input}>
            <TextInput
              disabled={discoveryCtx.loading}
              fullWidth
              value={brand}
              label={discoveryType === "brand" ? "Brand" : "Product"}
              placeholder={
                discoveryType === "brand" ? "Nike" : "Nike Air Force 1"
              }
              onChange={handleBrandInputChange}
            />

            {loading && (
              <CircularProgress className={classes.loading} size={15} />
            )}
            {suggestions.length > 0 && (
              <span className={classes.hint_text}>
                Did you mean:
                <i
                  className={classes.hint_chip}
                  onClick={() => handleSuggestionClick(suggestions[0])}
                >
                  {suggestions[0]}
                </i>
              </span>
            )}
          </div>

          <MUISelect
            loading={isCategorySuggestionLoading}
            disabled={discoveryCtx.loading}
            fullWidth
            placeholder="eg Athletic Apparel"
            optionValue={category}
            label="Category"
            sx={{
              color: category ? "black" : "#979797", // Gray when placeholder is visible
            }}
            options={[
              ...aICategories,
              {
                name: "Add Manually",
                value: "Add Manually",
                id: 0,
              },
            ]}
            onChange={(e) => {
              const value = e.target.value;
              console.log(value);
              if (value === "Add Manually") {
                if (brand.trim().length === 0) {
                  toast.warn("Please enter brand first");
                  return;
                }
                setManualCategoryInput("");
                setShowManualCategoryInput(true);
                return;
              }
              setCategory(value);
              resetState();
            }}
          />

          <div>
            <TextInput
              fullWidth
              label="Market"
              disabled={discoveryCtx.loading}
              placeholder="e.g North America"
              value={market}
              onChange={(e) => {
                // discoveryCtx.updateMarket(e.target.value);
                setMarket(e.target.value);
                resetState();
              }}
            />
          </div>

          <MUISelect
            disabled={discoveryCtx.loading}
            label={"LLM*"}
            defaultValue="gpt-4o"
            options={LLM_OPTIONS}
            optionValue={llm}
            onChange={(e) => {
              resetState();
              setLLM(e.target.value);
            }}
          />

          <div>
            {!discoveryCtx.loading && (
              <MUIButton
                style={{ width: "200px" }}
                variant="contained"
                color={discoveryCtx.loading ? "error" : "primary"}
                disabled={
                  loading || !category || discoveryCtx.is_launched || !brand
                }
                onClick={debounceLaunch}
              >
                Launch
              </MUIButton>
            )}
            {discoveryCtx.loading && (
              <MUIButton
                style={{ width: "200px" }}
                color="error"
                onClick={debounceLaunch}
              >
                Cancel
              </MUIButton>
            )}
          </div>
        </div>
      </div>

      {showManualCategoryInput && (
        <Modal
          title="Add Category"
          open={true}
          showActions
          handleClose={() => setShowManualCategoryInput(false)}
          handleConfirm={() => {
            console.log("Confirm: " + manualCategoryInput);
            setAICategories((prev) => [
              ...prev,
              {
                name: manualCategoryInput,
                value: manualCategoryInput,
              },
            ]);
            setCategory(manualCategoryInput);
            setShowManualCategoryInput(false);
          }}
          confirmBtnDisabled={!manualCategoryInput}
        >
          <TextInput
            className={classes.input}
            fullWidth
            type="text"
            value={manualCategoryInput}
            onChange={(e) => setManualCategoryInput(e.target.value)}
            placeholder={"Enter category"}
          />
        </Modal>
      )}
      <ToastContainer />
    </div>
  );
}
